import React, { useEffect, useState } from 'react';
import { Alert, Box, Typography, Paper, TextField, Button, List, ListItem, ListItemText, CircularProgress } from '@mui/material';
import { apiFetch } from '../utils/apiFetch.js';

export default function BookingChat({ bookingId, token, viewerRole = 'customer' }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!bookingId) return;
    const fetchMessages = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await apiFetch(`/api/bookings/${bookingId}/messages`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        const data = await res.json().catch(() => []);
        if (!res.ok) throw new Error(data.error || 'Unable to load this conversation.');
        setMessages(Array.isArray(data) ? data : []);
      } catch (loadError) {
        setMessages([]);
        setError(loadError.message || 'Unable to load this conversation.');
      } finally {
        setLoading(false);
      }
    };
    fetchMessages();
  }, [bookingId, token]);

  const handleSend = async () => {
    const message = input.trim();
    if (!message || sending) return;
    setSending(true);
    setError('');
    try {
      const res = await apiFetch(`/api/bookings/${bookingId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ message })
      });
      const data = await res.json().catch(() => []);
      if (!res.ok) throw new Error(data.error || 'Unable to send your message.');
      setMessages(Array.isArray(data) ? data : []);
      setInput('');
    } catch (sendError) {
      setError(sendError.message || 'Unable to send your message.');
    } finally {
      setSending(false);
    }
  };
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" fontWeight={800}>{viewerRole === 'mover' ? 'Conversation with customer' : 'Message your mover'}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>{viewerRole === 'mover' ? 'Keep move-day details and updates in one place.' : 'Ask questions and keep move-day details in one place.'}</Typography>
      <Paper sx={{ p: 2, mb: 2, minHeight: 130, bgcolor: '#F8FAFC' }}>
        {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : messages.length === 0 ? <Typography variant="body2" color="text.secondary">No messages yet. Start the conversation when you need to coordinate a detail.</Typography> : (
          <List disablePadding>
            {messages.map((msg, idx) => {
              const senderName = msg.sender?.name || (msg.senderRole === 'mover' ? 'Mover' : 'Customer');
              return <ListItem key={msg._id || idx} disableGutters alignItems="flex-start"><ListItemText primary={msg.message} secondary={`${senderName} • ${new Date(msg.createdAt || Date.now()).toLocaleString()}`} /></ListItem>;
            })}
          </List>
        )}
      </Paper>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }} size="small" fullWidth multiline maxRows={3} placeholder={viewerRole === 'mover' ? 'Message the customer...' : 'Message your mover...'} inputProps={{ maxLength: 2000 }} />
        <Button variant="contained" onClick={handleSend} disabled={sending || !input.trim()}>{sending ? 'Sending...' : 'Send'}</Button>
      </Box>
    </Box>
  );
}
